'use client';

import React, { useState } from 'react';
import { useForm } from 'react-hook-form';
import { z } from 'zod';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslation, Trans } from 'react-i18next';
import { useHistory } from 'react-router-dom/cjs/react-router-dom';
import useFetch from 'use-http';
import {
  useUserDispatch,
  useUserState,
} from '@/components/context/UserContext';

import { Button } from '@/components/ui/button';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader } from '@/components/ui/card';
import { Heading } from '@/components/ui/shared/heading';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

import { AlertTriangle } from 'lucide-react';
import { set } from 'lodash';

const formSchema = z
  .object({
    password: z
      .string()
      .min(1, { message: 'Slaptažodis yra privalomas.' })
      .regex(
        /(?=[A-Za-z0-9@$!%*#?&^`~+\-]+$)^(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9])(?=.*[@$!%*#?&^`~+\-])(?=.{8,}).*$/,
        { message: 'Slaptažodis turi būti pakankamai stiprus.' },
      ),
    confirmPassword: z
      .string()
      .min(1, { message: 'Pakartotinas slaptažodis yra privalomas.' })
      .trim(),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: 'Slaptažodžiai nesutampa.',
    path: ['confirmPassword'],
  });

export default function ChangePasswordForm() {
  const { t } = useTranslation();
  const history = useHistory();
  const userState = useUserState();
  const sessionDispatch = useUserDispatch();
  const [loading, setLoading] = useState(false);
  const [previewModalVisible, setPreviewModalVisible] = useState(false);

  const {
    post: updateUserAccountRequest,
    response: updateUserAccountResponse,
  } = useFetch('/UserAccount/Update');

  const form = useForm({
    resolver: zodResolver(formSchema),
    defaultValues: {
      password: '',
      confirmPassword: '',
    },
  });

  async function onSubmit(data) {
    setLoading(true);
    const response = await updateUserAccountRequest('', {
      id: userState.id,
      email: userState.email,
      firstName: userState.firstName,
      lastName: userState.lastName,
      phoneNumber: userState.phoneNumber,
      password: data.password,
    });

    if (updateUserAccountResponse.ok) {
      sessionDispatch({
        type: 'UPDATE',
        data: {
          id: response.id,
          email: response.email,
          phoneNumber: response.phoneNumber,
          personalCode: userState.personalCode,
          firstName: response.firstName,
          lastName: response.lastName,
          countryCode: userState.countryCode,
          phoneNumberConfirmed: response.phoneNumberConfirmed,
          contacts: response.contacts,
        },
      });
      setPreviewModalVisible(true);
    }
    setLoading(false);
  }

  return (
    <>
      <Card>
        <CardHeader className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
          <div>
            <Heading variant={'userAccountTitle'}>
              {t('userAccount.userInfoTab.loginInfoTitle')}
            </Heading>
          </div>
          <div className="hidden sm:block">
            <Button type="submit" form="credentials-form">
              {t('userAccount.userInfoTab.updateData')}
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          <div className="mb-4 flex items-start gap-2 text-sm text-muted-foreground">
            <AlertTriangle className="h-5 w-5 mt-0.5 text-orange-500" />
            <span>
              <Trans i18nKey="validation.passwordStrongSymbols" />
            </span>
          </div>

          <Form {...form}>
            <form
              id="credentials-form"
              onSubmit={form.handleSubmit(onSubmit)}
              className="space-y-4"
            >
              <FormField
                control={form.control}
                name="password"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('userAccount.userCredentialsEdit.newPassword')}
                    </FormLabel>
                    <FormControl>
                      <Input type="password" autoFocus {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="confirmPassword"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>
                      {t('userAccount.userCredentialsEdit.confirmPassword')}
                    </FormLabel>
                    <FormControl>
                      <Input type="password" {...field} />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="block sm:hidden">
                <Button loading={loading} className="w-full" type="submit">
                  {t('userAccount.userInfoTab.updateData')}
                </Button>
              </div>
            </form>
          </Form>
        </CardContent>
      </Card>

      <Dialog open={previewModalVisible} onOpenChange={setPreviewModalVisible}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>
              {t('userAccount.userCredentialsEdit.changedPassword')}
            </DialogTitle>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant={'secondary'}
              onClick={() => {
                setPreviewModalVisible(false);
                history.push('/userAccount');
              }}
            >
              Gerai
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
